import React, { useState, useMemo } from 'react'

import { useEventContext } from 'context/EventContext'
import debounce from 'helpers/debounce'
import openUrl from 'helpers/openUrl'

import AutocompleInput from './components/AutocompleInput'

export const NO_SUGGESTION_TEXT = 'Make sure your keywords are spelled correctly or try another search.'
const MIN_INPUT_REQUIRED_LENGTH = 3
const SUGGESTIONS_EMPTY = []
const SUGGESTIONS_NOT_FOUND = [{ name: NO_SUGGESTION_TEXT, disabled: true }]
const API_COMPARE_SEARCH = '/compare/search'

const customFilterOptions = (currentOptions, currentParams) => {
  if (currentOptions.length === 1 && currentOptions[0].disabled) {
    return [
      {
        inputValue: currentParams.inputValue,
        ...currentOptions[0],
      },
    ]
  }
  return currentOptions.filter(
    option => currentParams.getOptionLabel(option).indexOf(currentParams.inputValue.trim()) > -1
  )
}

export const buildCompareUrl = (selectedSlug = '', currentVersusSlugs = []) => {
  const { href } = window.location
  const newVersusSlugs = `${[...currentVersusSlugs, selectedSlug].join('-vs-')}/`
  return selectedSlug ? `${href.replace(/\/compare\/.*/, '/compare/')}${newVersusSlugs}` : '#'
}

const fetchListings = query =>
  fetch(`${API_COMPARE_SEARCH}?q=${encodeURIComponent(query)}`).then(response => response.json() || [])

// TODO We dont need listings as a property. Let's use CompareContext in order to get data.
// This is what i call a smart component. Could also be a subcomponent from AddToCompare.
/** @type {import("./types").default} */
const CompareSearchBar = ({ placeholder, listings = [] }) => {
  const [suggestions, setSuggestions] = useState(SUGGESTIONS_EMPTY)
  const [latestSearch, setLatestSearch] = useState(null)
  const { sendEvent } = useEventContext()

  const slugs = useMemo(() => {
    const pageSlugs = listings.map(listing => listing.slug) || []
    // removing the 1st element of the array as it contains the listing used for comparison
    const versusSlugs = pageSlugs.slice(1) || []
    return {
      page: pageSlugs,
      versus: versusSlugs,
    }
  }, [listings])

  const fetchSuggestions = async inputValue => {
    if (latestSearch === inputValue) {
      return
    }
    try {
      const newSuggestions = await fetchListings(inputValue)
      setLatestSearch(inputValue)
      const filteredSuggestions = newSuggestions.filter(({ slug }) => !slugs.page.includes(slug))
      if (filteredSuggestions.length === 0) {
        setSuggestions(SUGGESTIONS_NOT_FOUND)
      } else {
        setSuggestions(filteredSuggestions)
      }
    } catch (err) {
      setSuggestions(SUGGESTIONS_NOT_FOUND)
    }
  }

  const handleInputChange = value => {
    const inputValue = value.trim()
    if (!inputValue) {
      setSuggestions(SUGGESTIONS_EMPTY)
    } else if (inputValue.length >= MIN_INPUT_REQUIRED_LENGTH) {
      fetchSuggestions(inputValue)
    }
  }

  const handleSuggestionClear = () => {
    setSuggestions(SUGGESTIONS_EMPTY)
    setLatestSearch(null)
  }

  const handleSuggestionSelected = (event, suggestion) => {
    sendEvent({
      engagementComponent: 'add-to-compare',
      engagementDestination: 'compare_add-product',
      engagementDetails: 'serp-item_product',
      name: 'engagement_facet_click',
    })

    openUrl(buildCompareUrl(suggestion.slug, slugs.versus), { target: '_self' })
  }

  return (
    <AutocompleInput
      className="SearchBar"
      onClear={handleSuggestionClear}
      onChange={debounce(handleInputChange, 300)}
      onSelect={handleSuggestionSelected}
      options={suggestions}
      getOptionDisabled={option => option.disabled}
      filterOptions={customFilterOptions}
      placeholder={placeholder}
    />
  )
}

export default CompareSearchBar
